import { Typography, Card, Breadcrumb } from 'antd';
import { Link } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import './LegalPages.css';

const { Title, Paragraph, Text } = Typography;

const Section = ({ title, children }) => (
  <>
    <Title level={4} style={{ color: 'var(--wl-primary)', marginTop: 32 }}>{title}</Title>
    {children}
  </>
);

const TermsAndConditions = () => (
  <div className="legal-page">
    <div className="legal-page-inner">
      <Breadcrumb items={[
        { title: <Link to="/login"><HomeOutlined /> Home</Link> },
        { title: 'Terms of Use' },
      ]} style={{ marginBottom: 24 }} />

      <Card bordered={false} className="legal-card">
        <Typography>
          <Title level={2} style={{ color: 'var(--wl-primary)', marginBottom: 4 }}>WeCircle — Terms of Use</Title>
          {/* 1 */}
          <Section title="1. Acceptance of Terms">
            <Paragraph>
              By downloading, installing, or using the WeCircle mobile application (&quot;Application&quot; or &quot;App&quot;),
              you confirm that you have read, understood, and agree to be bound by these Terms of Use (&quot;Terms&quot;).
              If you do not agree to these Terms, you must not use the Application.
            </Paragraph>
            <Paragraph>
              These Terms constitute a binding legal agreement between you (&quot;User,&quot; &quot;Parent,&quot; or
              &quot;Guardian&quot;) and We Little (&quot;We,&quot; &quot;Us,&quot; or &quot;Our&quot;).
            </Paragraph>
          </Section>

          {/* 2 */}
          <Section title="2. About WeCircle">
            <Paragraph>
              WeCircle is a clinic-centred paediatric oral health management platform operated by We Little. The Application
              is designed to allow authorised parent users to view their child&apos;s dental health records — specifically,
              the Smile Passport — published by their child&apos;s registered dental care provider (&quot;Doctor&quot;)
              through the platform.
            </Paragraph>
            <Paragraph>
              WeCircle is not a general-purpose health application. It is a closed, clinic-administered platform. Access is
              granted by invitation only, following account creation by an authorised clinic or platform administrator.
            </Paragraph>
          </Section>

          {/* 3 */}
          <Section title="3. Eligibility and Account Access">
            <Title level={5} style={{ marginTop: 16 }}>3.1 Who May Use This Application</Title>
            <Paragraph>
              The WeCircle Parent Application is accessible exclusively to parents or legal guardians of children registered
              with a We Little clinic partner. Access is granted solely through an account created by an authorised clinic
              administrator or Super Admin. You cannot self-register for an account.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>3.2 Your Account</Title>
            <Paragraph>
              Your account is personal to you and must not be shared with, transferred to, or accessed by any other individual.
              You are responsible for maintaining the confidentiality of your registered mobile number and any session credentials.
              You must notify We Little immediately if you become aware of any unauthorised access to your account. Authenticated
              sessions expire automatically after 15 minutes of inactivity as a security measure.
            </Paragraph>
          </Section>

          {/* 4 */}
          <Section title="4. Scope of Parent Access">
            <Title level={5} style={{ marginTop: 16 }}>4.1 Read-Only Access to Smile Passport</Title>
            <Paragraph>
              As a parent user, your access to the WeCircle Application is strictly read-only. You may view clinical records —
              known as the Smile Passport — that have been authored and published by your child&apos;s Doctor. You cannot create,
              edit, modify, annotate, share, or download any clinical content through the Application. Unpublished records are
              not visible to parents under any circumstances.
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>4.2 Emergency and Appointment Contact Touchpoints</Title>
            <Paragraph>
              The Application provides two contact touchpoints — an SOS contact and an Appointment contact — which display the
              registered phone number of your child&apos;s assigned clinic. These features display a contact number only. They do
              not connect you to an emergency response system, create a clinical record, initiate a booking, or trigger any backend
              workflow. Any follow-up is your responsibility via a direct phone call to the clinic.
            </Paragraph>
            <Paragraph>
              <Text strong>In a medical emergency, contact emergency services immediately. Do not rely on the SOS contact feature
              as an emergency response mechanism.</Text>
            </Paragraph>

            <Title level={5} style={{ marginTop: 16 }}>4.3 No Self-Service Clinical Data Entry</Title>
            <Paragraph>
              Parents are not permitted to enter, upload, or contribute any clinical data through this Application. All clinical
              records are created exclusively by authorised Doctors registered on the WeCircle platform.
            </Paragraph>
          </Section>

          {/* 5 */}
          <Section title="5. Prohibited Activities">
            <Paragraph>You agree not to:</Paragraph>
            <ul>
              <li>Attempt to gain access to any account, record, or data beyond your authorised scope</li>
              <li>Share, forward, reproduce, or distribute any clinical content accessible through the Application to any third party without authorisation</li>
              <li>Use the Application for any commercial, research, or third-party purpose</li>
              <li>Attempt to reverse-engineer, decompile, tamper with, or introduce malicious code into any part of the Application</li>
              <li>Use the Application in any manner that may disrupt its operation or the access of other authorised users</li>
              <li>Misrepresent your identity or your relationship to any child registered on the platform</li>
              <li>Use the Application in any manner that violates applicable Indian law or regulation</li>
            </ul>
          </Section>

          {/* 6 */}
          <Section title="6. No Medical Advice">
            <Paragraph>
              WeCircle is a clinical record delivery platform. Nothing accessible through this Application constitutes medical
              advice, a diagnosis, a treatment recommendation, or a substitute for professional dental or medical consultation.
            </Paragraph>
            <Paragraph>
              The clinical records visible to you have been authored by your child&apos;s registered Doctor. We Little does not
              author, review, clinically validate, or take responsibility for any clinical content published through the platform.
              For any clinical concerns, you must consult your child&apos;s registered dental care provider directly.
            </Paragraph>
          </Section>

          {/* 7 */}
          <Section title="7. Intellectual Property">
            <Paragraph>
              All software, design, content structure, interface elements, and platform architecture associated with WeCircle
              are the intellectual property of We Little or its licensors. You are granted a limited, non-exclusive,
              non-transferable, revocable licence to use the Application solely for the purposes described in these Terms.
            </Paragraph>
            <Paragraph>
              You may not copy, reproduce, modify, distribute, or create derivative works from any part of the Application
              or its content without prior written consent from We Little.
            </Paragraph>
          </Section>

          {/* 8 */}
          <Section title="8. Third-Party Services">
            <Paragraph>
              The Application uses third-party services for functionality such as SMS delivery and cloud infrastructure.
              We Little is not responsible for the privacy practices, terms, or reliability of any third-party services
              used to support the Application. Use of these services is subject to the relevant third-party terms.
            </Paragraph>
          </Section>

          {/* 9 */}
          <Section title="9. Disclaimers">
            <Paragraph>
              The Application is provided on an &quot;as is&quot; and &quot;as available&quot; basis. To the extent permitted
              by applicable law, We Little makes no warranties — express or implied — regarding the accuracy, reliability,
              availability, or fitness for purpose of the Application or any content within it.
            </Paragraph>
            <Paragraph>
              We Little does not warrant that the Application will be uninterrupted, error-free, or free from security
              vulnerabilities at all times.
            </Paragraph>
          </Section>

          {/* 10 */}
          <Section title="10. Limitation of Liability">
            <Paragraph>
              To the fullest extent permitted under applicable Indian law, We Little shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages arising from your use of or inability to use the
              Application, including but not limited to reliance on any clinical content accessed through the Application
              or any failure or delay in the SOS or Appointment contact touchpoints.
            </Paragraph>
          </Section>

          {/* 11 */}
          <Section title="11. Termination">
            <Paragraph>
              We Little reserves the right to suspend or terminate your access to the Application at any time, with or
              without notice, if you breach these Terms or if your account is deactivated by an authorised platform
              administrator. Upon termination, your right to access the Application ceases immediately.
            </Paragraph>
          </Section>

          {/* 12 */}
          <Section title="12. Governing Law and Dispute Resolution">
            <Paragraph>
              These Terms are governed by and construed in accordance with the laws of India. Any dispute arising from or
              in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in
              Coimbatore, Tamil Nadu.
            </Paragraph>
          </Section>

          {/* 13 */}
          <Section title="13. Changes to These Terms">
            <Paragraph>
              We Little reserves the right to update or revise these Terms at any time. Where changes are material, we will
              make reasonable efforts to notify you through the Application. Continued use of the Application following any
              update constitutes your acceptance of the revised Terms. The version number and effective date at the top of
              this document will always reflect the current Terms.
            </Paragraph>
          </Section>

          {/* 14 */}
          <Section title="14. Contact Us">
            <Paragraph>
              For any questions regarding these Terms of Use, contact us at:
            </Paragraph>
            <Paragraph>
              <Text strong>We Little</Text><br />
              739, Avinashi Road, 3rd Floor, Sri Ram Towers, Near Anna Statue, Coimbatore – 641018<br />
              Email: [INSERT CONTACT EMAIL]<br />
              Phone: [INSERT CONTACT NUMBER]
            </Paragraph>
          </Section>

          <div style={{ textAlign: 'center', marginTop: 40, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              WeCircle — Terms of Use &nbsp;|&nbsp; We Little &nbsp;|&nbsp; Version 1.0
            </Text>
          </div>
        </Typography>
      </Card>
    </div>
  </div>
);

export default TermsAndConditions;
